/*
Government Purpose Rights (“GPR”)
Contract No.  W911NF-14-D-0005
Contractor Name:   University of Southern California
Contractor Address:  3720 S. Flower Street, 3rd Floor, Los Angeles, CA 90089-0001
Expiration Date:  Restrictions do not expire, GPR is perpetual
Restrictions Notice/Marking: The Government's rights to use, modify, reproduce, release, perform, display, or disclose this software are restricted by paragraph (b)(2) of the Rights in Noncommercial Computer Software and Noncommercial Computer Software Documentation clause contained in the above identified contract.  No restrictions apply after the expiration date shown above. Any reproduction of the software or portions thereof marked with this legend must also reproduce the markings. (see: DFARS 252.227-7014(f)(2)) 
No Commercial Use: This software shall be used for government purposes only and shall not, without the express written permission of the party whose name appears in the restrictive legend, be used, modified, reproduced, released, performed, or displayed for any commercial purpose or disclosed to a person other than subcontractors, suppliers, or prospective subcontractors or suppliers, who require the software to submit offers for, or perform, government contracts.  Prior to disclosing the software, the Contractor shall require the persons to whom disclosure will be made to complete and sign the non-disclosure agreement at 227.7103-7.  (see DFARS 252.227-7025(b)(2))
*/

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  ActivityType,
  Focus,
  Goal,
  LearnerRecord,
  Lesson,
  Plan,
  Topic,
  TopicAttributeRelevance,
  User,
  UserAccessToken,
  UserPlans,
  UserReflection,
  UserTest,
  Verb,
} from "@ictlearningsciences/pal3-core/types";
import * as api from "@ictlearningsciences/pal3-core/api";
import * as xapi from "@ictlearningsciences/pal3-core/xapi";
import {
  copyAndRemove,
  copyAndSet,
  getFormattedDate,
} from "@ictlearningsciences/pal3-core/helpers";
import {
  CalculateStudyPace,
  GetStudyPace,
  StudyPace,
  StudyPaces,
} from "@ictlearningsciences/pal3-core/studypace";
import { GetUserGoal } from "@ictlearningsciences/pal3-core/usergoal";
import {
  GetMasteryForGoal,
  GetMasteryForTopic,
  MASTERY_MASTERED,
  OverrideGetMasteryForGoal,
  OverrideGetMasteryForTopic,
  TopicMasteryData,
} from "@ictlearningsciences/pal3-core/mastery";

import {
  getFormattedDateCompletedSurveyDate,
  LoadingState,
  LoadStatus,
} from "../../../helpers";
import { RootState } from "../..";

export interface StudyPaceState {
  target: StudyPace;
  lessonTotalMins: number;
  studyPace: StudyPace;
  studyPaceMins: number;
  studyPaceDays: number;
  masteryTargetValue?: number;
  masteryTargetDate?: number;
}

export interface UserSurveyData {
  variables: TopicAttributeRelevance[];
  completedSurveys: Record<string, string>;
  currentQuestion: number;
}

export interface UserDataState {
  // login
  user?: User;
  accessToken?: string;
  loginStatus: LoadingState;
  // records
  records: LearnerRecord[];
  unsavedRecords: LearnerRecord[];
  recordLoadState: LoadingState;
  recordSaveState: LoadingState;
  recordsProcessed: boolean;
  // study pace
  studyPace: StudyPaceState;
  // user goal
  activeGoal: Goal | undefined;
  activeFocus: Focus | undefined;
  activeTopic: Topic | undefined;
  activePlan: Plan | undefined;
  // user mastery
  goalMastery: Record<string, TopicMasteryData>;
  topicMastery: Record<string, TopicMasteryData>;
  // user plans
  plans: Plan[];
  planLoadState: LoadingState;
  planSaveState: LoadingState;
  // survey
  surveyRecords?: UserSurveyData;
  // user tests
  userTests: UserTest[];
  userReflections: UserReflection[];
  userTestLoadState: LoadingState;
  userTestSaveState: LoadingState;
}

const initialState: UserDataState = {
  // login
  loginStatus: { status: LoadStatus.NONE },
  // records
  records: [],
  unsavedRecords: [],
  recordLoadState: { status: LoadStatus.NONE },
  recordSaveState: { status: LoadStatus.NONE },
  recordsProcessed: false,
  // study pace
  studyPace: {
    target: StudyPaces.CASUAL,
    studyPace: StudyPaces.IDLE,
    studyPaceMins: 0,
    studyPaceDays: 1,
    lessonTotalMins: 0,
  },
  // user goal
  activeGoal: undefined,
  activeFocus: undefined,
  activeTopic: undefined,
  activePlan: undefined,
  // user mastery
  goalMastery: {},
  topicMastery: {},
  // plans
  plans: [],
  planLoadState: { status: LoadStatus.NONE },
  planSaveState: { status: LoadStatus.NONE },
  // self tests
  userTests: [],
  userReflections: [],
  userTestLoadState: { status: LoadStatus.NONE },
  userTestSaveState: { status: LoadStatus.NONE },
};

async function calcMasteryForTopic(
  records: LearnerRecord[],
  user: User,
  topic: Topic,
  goal: Goal
): Promise<TopicMasteryData> {
  let mastery: TopicMasteryData = { mastery: 0, isMastered: false };
  try {
    mastery = await OverrideGetMasteryForTopic(topic, records, user, goal);
  } catch (err) {
    mastery = GetMasteryForTopic(topic, records);
  }
  if (topic.recommender === "linear_required" && topic.lessons.length === 0) {
    mastery.isMastered = false;
  }
  return mastery;
}

async function calcMasteryForGoal(
  records: LearnerRecord[],
  user: User,
  goal: Goal
): Promise<TopicMasteryData> {
  let mastery: TopicMasteryData = { mastery: 0, isMastered: false };
  try {
    mastery = await OverrideGetMasteryForGoal(goal, records, user);
  } catch (err) {
    mastery = GetMasteryForGoal(goal, records);
  }
  for (const f of goal.focusList) {
    for (const t of f.topics) {
      if (t.recommender === "linear_required" && t.lessons.length === 0) {
        mastery.isMastered = false;
        return mastery;
      }
    }
  }
  return mastery;
}

/** Actions */
export const login = createAsyncThunk(
  "userData/login",
  async (args: {
    username: string;
    password: string;
  }): Promise<UserAccessToken> => {
    return await api.login(args.username, args.password);
  }
);

export const signup = createAsyncThunk(
  "userData/signup",
  async (args: {
    username: string;
    name: string;
    email: string;
    password: string;
  }): Promise<UserAccessToken> => {
    return await api.signup(
      args.username,
      args.name,
      args.email,
      args.password
    );
  }
);

export const resetPassword = createAsyncThunk(
  "userData/resetPassword",
  async (args: { token: string; email: string }): Promise<UserAccessToken> => {
    return await api.resetPassword(args.token, args.email);
  }
);

export const loadRecords = createAsyncThunk(
  "userData/loadRecords",
  async (since: string, thunkAPI): Promise<LearnerRecord[] | undefined> => {
    const state = thunkAPI.getState() as RootState;
    const endedAt = state.userData.recordLoadState?.endedAt;
    const accessToken = state.userData.accessToken;
    if (!accessToken) {
      throw new Error("not logged in");
    }
    const date = since || endedAt || "1971-01-01T00:00:00.000Z";
    return await xapi.loadRecords(accessToken, date);
  }
);

export const saveRecords = createAsyncThunk(
  "userData/saveRecords",
  async (
    _args,
    thunkAPI
  ): Promise<
    Record<string, { saved: boolean; record?: LearnerRecord }> | undefined
  > => {
    const state = thunkAPI.getState() as RootState;
    const unsavedRecords = state.userData.unsavedRecords;
    const accessToken = state.userData.accessToken;
    if (!accessToken) {
      return undefined;
    }
    return await xapi.saveRecords(accessToken, unsavedRecords);
  }
);

export const resetRecords = createAsyncThunk(
  "userData/resetRecords",
  async (
    date: Date | undefined,
    thunkAPI
  ): Promise<LearnerRecord[] | undefined> => {
    const state = thunkAPI.getState() as RootState;
    const accessToken = state.userData.accessToken;
    if (!accessToken) {
      throw new Error("no access token");
    }
    await api.resetUserRecords(accessToken, date);
    return await xapi.loadRecords(
      accessToken,
      date ? getFormattedDate(date) : "1971-01-01T00:00:00.000Z"
    );
  }
);

export const loadUserPlans = createAsyncThunk(
  "userData/userPlans",
  async (_args, thunkAPI): Promise<UserPlans> => {
    const state = thunkAPI.getState() as RootState;
    const accessToken = state.userData.accessToken;
    if (!accessToken) {
      throw new Error("not logged in");
    }
    return await api.fetchUserPlans(accessToken);
  }
);

export const saveUserPlan = createAsyncThunk(
  "userData/saveUserPlan",
  async (plan: Plan, thunkAPI): Promise<UserPlans> => {
    const state = thunkAPI.getState() as RootState;
    const accessToken = state.userData.accessToken;
    if (!accessToken) {
      throw new Error("not logged in");
    }
    return await api.saveUserPlan(accessToken, plan);
  }
);

export const loadUserTests = createAsyncThunk(
  "data/loadUserTests",
  async (
    _args,
    thunkAPI
  ): Promise<{ tests: UserTest[]; reflections: UserReflection[] }> => {
    const state = thunkAPI.getState() as RootState;
    const accessToken = state.userData.accessToken;
    if (!accessToken) {
      throw new Error("not logged in");
    }
    const tests = await api.fetchUserTests(accessToken);
    const reflections = await api.fetchUserReflections(accessToken);
    return { tests, reflections };
  }
);

export const saveUserTest = createAsyncThunk(
  "userData/saveUserTest",
  async (test: UserTest, thunkAPI): Promise<UserTest> => {
    const state = thunkAPI.getState() as RootState;
    const accessToken = state.userData.accessToken;
    if (!accessToken) {
      throw new Error("not logged in");
    }
    return await api.saveUserTest(accessToken, test);
  }
);

export const saveUserTests = createAsyncThunk(
  "userData/saveUserTests",
  async (tests: UserTest[], thunkAPI): Promise<UserTest[]> => {
    const state = thunkAPI.getState() as RootState;
    const accessToken = state.userData.accessToken;
    if (!accessToken) {
      throw new Error("not logged in");
    }
    const savedTests = [];
    for (const test of tests) {
      const savedTest = await api.saveUserTest(accessToken, test);
      savedTests.push(savedTest);
    }
    return savedTests;
  }
);

export const deleteUserTest = createAsyncThunk(
  "userData/deleteUserTest",
  async (test: UserTest, thunkAPI): Promise<boolean> => {
    const state = thunkAPI.getState() as RootState;
    const accessToken = state.userData.accessToken;
    if (!accessToken) {
      throw new Error("not logged in");
    }
    return await api.deleteUserTest(accessToken, test.id);
  }
);

export const saveUserReflection = createAsyncThunk(
  "userData/saveUserReflection",
  async (
    args: { lesson: Lesson; notes: string },
    thunkAPI
  ): Promise<UserReflection> => {
    const state = thunkAPI.getState() as RootState;
    const accessToken = state.userData.accessToken;
    if (!accessToken) {
      throw new Error("not logged in");
    }
    return await api.saveUserReflection(
      accessToken,
      args.lesson.id,
      args.notes
    );
  }
);

export const joinStudy = createAsyncThunk(
  "userData/joinStudy",
  async (code: string, thunkAPI): Promise<User> => {
    const state = thunkAPI.getState() as RootState;
    const accessToken = state.userData.accessToken;
    if (!accessToken) {
      throw new Error("not logged in");
    }
    return await api.joinStudy(accessToken, code);
  }
);

export const leaveStudy = createAsyncThunk(
  "userData/leaveStudy",
  async (code: string, thunkAPI): Promise<User> => {
    const state = thunkAPI.getState() as RootState;
    const accessToken = state.userData.accessToken;
    if (!accessToken) {
      throw new Error("not logged in");
    }
    return await api.leaveStudy(accessToken, code);
  }
);

export const onUpdateRecords = createAsyncThunk(
  "userData/onUpdateRecords",
  async (
    _args,
    thunkAPI
  ): Promise<{
    // study pace
    studyPace: StudyPaceState;
    // user goal
    activeGoal: Goal | undefined;
    activeFocus: Focus | undefined;
    activeTopic: Topic | undefined;
    activePlan: Plan | undefined;
    // user mastery
    goalMastery: Record<string, TopicMasteryData>;
    topicMastery: Record<string, TopicMasteryData>;
    // survey
    surveyRecords: UserSurveyData;
  }> => {
    const rootState = thunkAPI.getState() as RootState;
    const gqlData = {
      goals: rootState.data.goals,
      topics: rootState.data.topics,
      lessons: rootState.data.lessons,
      resources: rootState.data.resources,
    };
    const records = rootState.userData.records;
    const state = rootState.userData;
    const user = state.user;
    if (!user) {
      throw new Error("No user");
    }

    // update user goals
    const userGoalData = GetUserGoal(records, gqlData, state.plans);
    // update study pace
    const { lessonTotalMins, studyPace, studyPaceMins, studyPaceDays } =
      CalculateStudyPace(records);
    const target = GetStudyPace(records);
    const studyRecord = records.find(
      (r) =>
        r.verb === Verb.SELECTED && r.objType === ActivityType.STUDY_COMMITMENT
    );
    const mtd =
      studyRecord?.result?.extensions[
        "http://pal3.org/xapi/studypace/masteryTargetDate"
      ];
    // update survey
    const surveyRecords = records
      .filter(
        (l) =>
          l.objType === ActivityType.SURVEY &&
          l.verb === Verb.COMPLETED &&
          l.timestamp
      )
      .reverse(); // survey records in order
    const completedSurveys: Record<string, string> = state.surveyRecords
      ? { ...state.surveyRecords.completedSurveys }
      : {};
    const variables: TopicAttributeRelevance[] = state.surveyRecords
      ? [...state.surveyRecords.variables]
      : [];
    surveyRecords.forEach((r) => {
      completedSurveys[r.objId] = getFormattedDateCompletedSurveyDate(
        new Date(r.timestamp)
      );
      try {
        const result =
          r.result?.extensions["http://pal3.org/xapi/survey/completed"];
        if (result?.variables) {
          for (const v of result.variables as TopicAttributeRelevance[]) {
            const idx = variables.findIndex((vv) => vv.name === v.name);
            if (idx === -1) {
              variables.push(v);
            } else {
              variables[idx] = v;
            }
          }
        }
      } catch (err) {
        console.error("test failed to parse variables");
      }
    });
    // update user mastery
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const goalMastery: any = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const topicMastery: any = {};
    for (const goal of Object.values(gqlData.goals)) {
      goalMastery[goal.id] = await calcMasteryForGoal(records, user, goal);
      for (const focus of goal.focusList) {
        for (const topic of focus.topics) {
          const t = gqlData.topics[topic.id];
          topicMastery[topic.id] = await calcMasteryForTopic(
            records,
            user,
            t,
            goal
          );
        }
      }
    }

    return {
      activeGoal: userGoalData.activeGoal,
      activeFocus: userGoalData.activeFocus,
      activeTopic: userGoalData.activeTopic,
      activePlan: userGoalData.activePlan,
      studyPace: {
        studyPace: studyPace,
        studyPaceMins: studyPaceMins,
        studyPaceDays: studyPaceDays,
        lessonTotalMins: lessonTotalMins,
        target: target || initialState.studyPace.target,
        masteryTargetDate: studyRecord
          ? mtd && typeof mtd === "number"
            ? mtd
            : 0
          : initialState.studyPace.masteryTargetDate,
        masteryTargetValue: studyRecord
          ? studyRecord.result?.score || MASTERY_MASTERED
          : initialState.studyPace.masteryTargetValue,
      },
      surveyRecords: {
        variables: variables,
        completedSurveys: completedSurveys,
        currentQuestion: 0,
      },
      goalMastery,
      topicMastery,
    };
  }
);

export const userDataSlice = createSlice({
  name: "userData",
  initialState: initialState,
  reducers: {
    logout: (state) => {
      // login
      state.user = undefined;
      state.accessToken = undefined;
      state.loginStatus = initialState.loginStatus;
      // records
      state.records = initialState.records;
      state.unsavedRecords = initialState.unsavedRecords;
      state.recordLoadState = initialState.recordLoadState;
      state.recordSaveState = initialState.recordSaveState;
      state.recordsProcessed = false;
      // study pace
      try {
        state.studyPace.target = initialState.studyPace.target;
        state.studyPace.lessonTotalMins =
          initialState.studyPace.lessonTotalMins;
        state.studyPace.studyPace = initialState.studyPace.studyPace;
        state.studyPace.studyPaceMins = initialState.studyPace.studyPaceMins;
        state.studyPace.studyPaceDays = initialState.studyPace.studyPaceDays;
        state.studyPace.masteryTargetValue =
          initialState.studyPace.masteryTargetValue;
        state.studyPace.masteryTargetDate =
          initialState.studyPace.masteryTargetDate;
      } catch {
        state.studyPace = initialState.studyPace;
      }
      // user goal
      state.activeGoal = initialState.activeGoal;
      state.activeFocus = initialState.activeFocus;
      state.activeTopic = initialState.activeTopic;
      state.activePlan = initialState.activePlan;
      // user mastery
      state.goalMastery = initialState.goalMastery;
      state.topicMastery = initialState.topicMastery;
      // user plans
      state.plans = initialState.plans;
      state.planLoadState = initialState.planLoadState;
      state.planSaveState = initialState.planSaveState;
      // survey
      state.surveyRecords = initialState.surveyRecords;
      // user tests
      state.userTests = initialState.userTests;
      state.userReflections = initialState.userReflections;
      state.userTestLoadState = initialState.userTestLoadState;
      state.userTestSaveState = initialState.userTestSaveState;
    },
    addRecord: (state, action: PayloadAction<LearnerRecord>) => {
      if (!state.user) return;
      const record = action.payload;
      state.records.unshift(record);
      state.unsavedRecords.unshift(record);
    },
    updateSurveyVariables: (
      state,
      action: PayloadAction<{
        goal?: string;
        data: TopicAttributeRelevance[];
        currentQuestion: number;
      }>
    ) => {
      if (!state.user) return;
      const variables = state.surveyRecords?.variables || [];
      const completed = state.surveyRecords?.completedSurveys || {};
      if (action.payload.goal) {
        completed[action.payload.goal] = getFormattedDateCompletedSurveyDate(
          new Date()
        );
      }
      for (const variable of action.payload.data) {
        const idx = variables.findIndex((v) => v.name === variable.name);
        if (idx === -1) {
          variables.push(variable);
        } else {
          variables[idx] = variable;
        }
      }
      state.surveyRecords = {
        variables: variables,
        completedSurveys: completed,
        currentQuestion: action.payload.currentQuestion,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(onUpdateRecords.fulfilled, (state, action) => {
        if (!state.user) return;
        state.studyPace = action.payload.studyPace;
        state.activeGoal = action.payload.activeGoal;
        state.activeFocus = action.payload.activeFocus;
        state.activeTopic = action.payload.activeTopic;
        state.activePlan = action.payload.activePlan;
        state.surveyRecords = action.payload.surveyRecords;
        state.goalMastery = action.payload.goalMastery;
        state.topicMastery = action.payload.topicMastery;
        state.recordsProcessed = true;
      })
      // study
      .addCase(joinStudy.fulfilled, (state, action) => {
        if (state.user) {
          state.user.traits = action.payload.traits;
          state.user.studies = action.payload.studies;
        }
      })
      .addCase(leaveStudy.fulfilled, (state, action) => {
        if (state.user) {
          state.user.traits = action.payload.traits;
          state.user.studies = action.payload.studies;
        }
      })
      // login
      .addCase(login.pending, (state) => {
        state.loginStatus = {
          status: LoadStatus.IN_PROGRESS,
          startedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(login.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.accessToken = action.payload.accessToken;
        state.loginStatus = {
          status: LoadStatus.DONE,
          endedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(login.rejected, (state, action) => {
        state.loginStatus = {
          status: LoadStatus.FAILED,
          failedAt: getFormattedDate(),
          error: action.error.message,
        };
      })
      .addCase(signup.pending, (state) => {
        state.loginStatus = {
          status: LoadStatus.IN_PROGRESS,
          startedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.accessToken = action.payload.accessToken;
        state.loginStatus = {
          status: LoadStatus.DONE,
          endedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(signup.rejected, (state, action) => {
        state.loginStatus = {
          status: LoadStatus.FAILED,
          failedAt: getFormattedDate(),
          error: action.error.message,
        };
      })
      .addCase(resetPassword.pending, (state) => {
        state.loginStatus = {
          status: LoadStatus.IN_PROGRESS,
          startedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.accessToken = action.payload.accessToken;
        state.loginStatus = {
          status: LoadStatus.DONE,
          endedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loginStatus = {
          status: LoadStatus.FAILED,
          failedAt: getFormattedDate(),
          error: action.error.message,
        };
      })
      // records
      .addCase(loadRecords.pending, (state) => {
        state.recordLoadState = {
          status: LoadStatus.IN_PROGRESS,
          startedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(loadRecords.fulfilled, (state, action) => {
        if (!state.user || action.payload === undefined) return;
        // order the loaded records by latest on top
        const loadedRecords = action.payload.sort((a, b) => {
          return (
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );
        });

        for (const lr of loadedRecords) {
          const idx = state.records.findIndex((r) => r.id === lr.id);
          if (idx === -1) {
            state.records.unshift(lr);
          } else {
            state.records = copyAndSet(state.records, idx, lr);
          }
        }
        const rrt = state.user?.recordResetTime;
        if (rrt) {
          state.records = state.records.filter(
            (r) => new Date(r.timestamp) > new Date(rrt)
          );
        }
        state.recordLoadState = {
          status: LoadStatus.DONE,
          endedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(loadRecords.rejected, (state, action) => {
        state.recordLoadState = {
          status: LoadStatus.FAILED,
          failedAt: getFormattedDate(),
          error: action.error.message,
        };
      })
      .addCase(saveRecords.pending, (state) => {
        state.recordSaveState = {
          status: LoadStatus.IN_PROGRESS,
          startedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(saveRecords.fulfilled, (state, action) => {
        if (!state.user || action.payload === undefined) {
          return;
        }
        const savedRecords = action.payload;
        if (!process.env.CI) {
          for (const [k, v] of Object.entries(savedRecords)) {
            const idx = state.records.findIndex((r) => r.id === k);
            if (v.saved && v.record) {
              state.records[idx] = v.record;
            } else {
              state.records = copyAndRemove(state.records, idx);
            }
          }
        }
        const rrt = state.user?.recordResetTime;
        if (rrt) {
          state.records = state.records.filter(
            (r) => new Date(r.timestamp) > new Date(rrt)
          );
        }
        state.unsavedRecords = [];
        state.recordSaveState = {
          status: LoadStatus.DONE,
          endedAt: getFormattedDate(),
          error: undefined,
        };
        state.recordLoadState = {
          status: LoadStatus.DONE,
          endedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(saveRecords.rejected, (state, action) => {
        state.recordSaveState = {
          status: LoadStatus.FAILED,
          failedAt: getFormattedDate(),
          error: action.error.message,
        };
      })
      .addCase(resetRecords.fulfilled, (state, action) => {
        if (!state.user || !action.payload) return;
        const date = action.meta.arg;
        if (date) {
          state.user.recordResetTime = date;
          state.records = action.payload.filter(
            (r) => new Date(r.timestamp) > date
          );
        } else {
          state.records = action.payload;
        }
        state.recordLoadState = {
          status: LoadStatus.DONE,
          endedAt: getFormattedDate(),
          error: undefined,
        };
      })
      // plans
      .addCase(loadUserPlans.pending, (state) => {
        if (!state.user) return;
        state.planLoadState = {
          status: LoadStatus.IN_PROGRESS,
          startedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(loadUserPlans.fulfilled, (state, action) => {
        if (!state.user) return;
        state.plans = action.payload.plans;
        state.planLoadState = {
          status: LoadStatus.DONE,
          endedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(loadUserPlans.rejected, (state, action) => {
        if (!state.user) return;
        state.planLoadState = {
          status: LoadStatus.FAILED,
          failedAt: getFormattedDate(),
          error: action.error.message,
        };
      })
      .addCase(saveUserPlan.pending, (state) => {
        if (!state.user) return;
        state.planSaveState = {
          status: LoadStatus.IN_PROGRESS,
          startedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(saveUserPlan.fulfilled, (state, action) => {
        if (!state.user) return;
        state.plans = action.payload.plans;
        state.planSaveState = {
          status: LoadStatus.DONE,
          endedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(saveUserPlan.rejected, (state, action) => {
        if (!state.user) return;
        state.planSaveState = {
          status: LoadStatus.FAILED,
          failedAt: getFormattedDate(),
          error: action.error.message,
        };
      })
      // user tests
      .addCase(loadUserTests.pending, (state) => {
        if (!state.user) return;
        state.userTestLoadState = {
          status: LoadStatus.IN_PROGRESS,
          startedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(loadUserTests.fulfilled, (state, action) => {
        if (!state.user) return;
        state.userTests = action.payload.tests;
        state.userReflections = action.payload.reflections;
        state.userTestLoadState = {
          status: LoadStatus.DONE,
          endedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(loadUserTests.rejected, (state, action) => {
        if (!state.user) return;
        state.userTestLoadState = {
          status: LoadStatus.FAILED,
          failedAt: getFormattedDate(),
          error: action.error.message,
        };
      })

      .addCase(saveUserTest.pending, (state) => {
        if (!state.user) return;
        state.userTestSaveState = {
          status: LoadStatus.IN_PROGRESS,
          startedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(saveUserTest.fulfilled, (state, action) => {
        if (!state.user) return;
        const idx = state.userTests.findIndex(
          (u) => u.id === action.payload.id
        );
        if (idx === -1) {
          state.userTests.push(action.payload);
        } else {
          state.userTests = copyAndSet(state.userTests, idx, action.payload);
        }
        state.userTestSaveState = {
          status: LoadStatus.DONE,
          endedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(saveUserTest.rejected, (state, action) => {
        if (!state.user) return;
        state.userTestSaveState = {
          status: LoadStatus.FAILED,
          failedAt: getFormattedDate(),
          error: action.error.message,
        };
      })

      .addCase(saveUserTests.pending, (state) => {
        if (!state.user) return;
        state.userTestSaveState = {
          status: LoadStatus.IN_PROGRESS,
          startedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(saveUserTests.fulfilled, (state, action) => {
        if (!state.user) return;
        for (const test of action.payload) {
          const idx = state.userTests.findIndex((u) => u.id === test.id);
          if (idx === -1) {
            state.userTests.push(test);
          } else {
            state.userTests = copyAndSet(state.userTests, idx, test);
          }
        }
        state.userTestSaveState = {
          status: LoadStatus.DONE,
          endedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(saveUserTests.rejected, (state, action) => {
        if (!state.user) return;
        state.userTestSaveState = {
          status: LoadStatus.FAILED,
          failedAt: getFormattedDate(),
          error: action.error.message,
        };
      })

      .addCase(deleteUserTest.pending, (state) => {
        if (!state.user) return;
        state.userTestSaveState = {
          status: LoadStatus.IN_PROGRESS,
          startedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(deleteUserTest.fulfilled, (state, action) => {
        if (!state.user) return;
        state.userTests = state.userTests.filter(
          (s) => s.id !== action.meta.arg.id
        );
        state.userTestSaveState = {
          status: LoadStatus.DONE,
          endedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(deleteUserTest.rejected, (state, action) => {
        if (!state.user) return;
        state.userTestSaveState = {
          status: LoadStatus.FAILED,
          failedAt: getFormattedDate(),
          error: action.error.message,
        };
      })

      .addCase(saveUserReflection.pending, (state) => {
        if (!state.user) return;
        state.userTestSaveState = {
          status: LoadStatus.IN_PROGRESS,
          startedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(saveUserReflection.fulfilled, (state, action) => {
        if (!state.user) return;
        const idx = state.userReflections.findIndex(
          (u) => u.id === action.payload.id
        );
        if (idx === -1) {
          state.userReflections.push(action.payload);
        } else {
          state.userReflections = copyAndSet(
            state.userReflections,
            idx,
            action.payload
          );
        }
        state.userTestSaveState = {
          status: LoadStatus.DONE,
          endedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(saveUserReflection.rejected, (state, action) => {
        if (!state.user) return;
        state.userTestSaveState = {
          status: LoadStatus.FAILED,
          failedAt: getFormattedDate(),
          error: action.error.message,
        };
      });
  },
});

export const { logout, addRecord, updateSurveyVariables } =
  userDataSlice.actions;

export default userDataSlice.reducer;
